/**
 * Print a filled PostGrid letter through the browser at exact US Letter size.
 * The template is a fixed 816×1056px page (8.5×11in at 96dpi), so the page needs no margins.
 */

export const PRINT_LETTER_CSS = [
  '@page { size: letter; margin: 0; }',
  'html, body { margin: 0 !important; padding: 0 !important; ' +
    '-webkit-print-color-adjust: exact; print-color-adjust: exact; }',
  '.page:last-of-type { page-break-after: auto; break-after: auto; }'
].join('\n');

const STYLE_TAG = `<style data-print-letter>\n${PRINT_LETTER_CSS}\n</style>`;

/** Add the print CSS just before `</head>` (or create a head if the template has none). */
export function withPrintLetterCss(html: string): string {
  if (/<\/head>/i.test(html)) return html.replace(/<\/head>/i, `${STYLE_TAG}</head>`);
  if (/<html[^>]*>/i.test(html)) {
    return html.replace(/<html[^>]*>/i, (tag) => `${tag}<head>${STYLE_TAG}</head>`);
  }
  return `${STYLE_TAG}${html}`;
}

export const PRINT_LETTER_ASSET_TIMEOUT_MS = 5000;
const CLEANUP_FALLBACK_MS = 60000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Until the srcdoc document (not the initial about:blank) has finished loading. */
async function waitForSrcdoc(iframe: HTMLIFrameElement, timeoutMs: number): Promise<void> {
  const steps = Math.max(1, Math.ceil(timeoutMs / 50));
  for (let step = 0; step < steps; step += 1) {
    const doc = iframe.contentDocument;
    if (doc && doc.URL === 'about:srcdoc' && doc.readyState === 'complete') return;
    await delay(50);
  }
  throw new Error('The letter could not be prepared for printing.');
}

function waitForImages(doc: Document): Promise<void> {
  const pending = Array.from(doc.images)
    .filter((img) => !img.complete)
    .map(
      (img) =>
        new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        })
    );
  return Promise.all(pending).then(() => undefined);
}

/** Fonts and images, or the timeout, whichever is first. A missing logo never blocks printing. */
async function waitForAssets(doc: Document, timeoutMs: number): Promise<void> {
  const fonts = doc.fonts?.ready ? doc.fonts.ready.then(() => undefined) : Promise.resolve();
  await Promise.race([
    Promise.all([fonts, waitForImages(doc)]).then(() => undefined),
    delay(timeoutMs)
  ]);
}

/**
 * Render the letter in a hidden iframe and open the browser print dialog for it.
 * Scripts in the letter never run: the frame is sandboxed with only same-origin (so we can wait on
 * its fonts and images) and modals (so print() is allowed).
 * Resolves when the print dialog closes. The frame stays up until then so the letter is what prints.
 */
export async function printLetterHtml(
  filledHtml: string,
  { timeoutMs = PRINT_LETTER_ASSET_TIMEOUT_MS }: { timeoutMs?: number } = {}
): Promise<void> {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('sandbox', 'allow-same-origin allow-modals');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('tabindex', '-1');
  iframe.title = 'Print letter';
  // Off-screen but laid out. display:none frames print blank in some browsers.
  iframe.style.position = 'fixed';
  iframe.style.left = '-10000px';
  iframe.style.top = '0';
  iframe.style.width = '816px';
  iframe.style.height = '1056px';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';

  iframe.srcdoc = withPrintLetterCss(filledHtml);
  document.body.appendChild(iframe);

  let removed = false;
  const cleanup = () => {
    if (removed) return;
    removed = true;
    iframe.remove();
  };

  try {
    await waitForSrcdoc(iframe, timeoutMs);
    const frameWindow = iframe.contentWindow;
    const doc = iframe.contentDocument;
    if (!frameWindow || !doc) throw new Error('The letter could not be prepared for printing.');
    await waitForAssets(doc, timeoutMs);
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve();
      };
      // afterprint fires when the dialog closes, whether they print or cancel.
      frameWindow.addEventListener('afterprint', () => setTimeout(finish, 0), { once: true });
      setTimeout(finish, CLEANUP_FALLBACK_MS);
      frameWindow.focus();
      frameWindow.print();
    });
  } catch (error) {
    cleanup();
    throw error;
  }
}
